<div class="flex flex-1 overflow-hidden gap-4 p-4">
  <div class="flex flex-col flex-1 min-w-0 overflow-hidden gap-3">
    @if (hasReplacements() && !executedRulesBannerDismissed()) {
      <p-message
        class="shrink-0 px-1"
        severity="info"
        [closable]="true"
        (onClose)="executedRulesBannerDismissed.set(true)"
      >
        <div class="flex flex-col gap-1.5 min-w-0">
          <span class="text-body-sm-600">Floor plan {{ allExecutions().length > 1 ? 'rules' : 'rule' }} executed</span>
          <ul class="m-0 p-0 list-none flex flex-col gap-1 text-body-xs">
            @for (execution of allExecutions(); track execution.executionId) {
              <li class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <span class="font-semibold">{{ executionLabel(execution) }}:</span>
                <span class="line-through opacity-75">{{ roomNames(execution.replacedRooms) }}</span>
                <i class="pi pi-arrow-right" style="font-size: 0.65rem"></i>
                <span class="font-semibold">{{ roomNames(execution.newRooms) }}</span>
              </li>
            }
          </ul>
        </div>
      </p-message>
    }
    @if (isLoading()) {
      <p class="text-body-sm text-surface-500 m-0">Loading review list...</p>
    } @else if (!hasReplacements()) {
      <div
        class="border border-slate-200 border-dashed rounded-md flex flex-1 items-center justify-center text-center p-8"
      >
        <p class="text-body-sm text-surface-500 m-0">
          No room replacements have run on this deal yet. This tab fills in once a plan-changing option locks and swaps
          the deal's rooms.
        </p>
      </div>
    } @else {
      <div class="flex flex-col gap-2 shrink-0">
        @for (replacement of replacements(); track replacement.executionId) {
          <div class="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-red-700">
            <i class="pi pi-exclamation-circle text-body-sm mt-0.5"></i>
            <p class="text-body-xs m-0 flex-1">
              <span class="font-semibold">{{ replacement.triggerProductCode || 'A plan change' }}</span>
              was selected which replaced
              <span class="font-semibold">{{ roomNames(replacement.replacedRooms) }}</span>
              with
              <span class="font-semibold">{{ roomNames(replacement.newRooms) }}</span
              >. Options with mismatched locations are highlighted below — re-configure them to move them back to
              Addendums.
            </p>
          </div>
        }
      </div>

      <div class="overflow-y-auto flex-1 flex flex-col gap-3">
        @if (resolvedGroups().length === 0) {
          <div class="border border-slate-200 rounded-md flex flex-1 items-center justify-center text-center p-8">
            <p class="text-body-sm text-surface-500 m-0">
              No selections are currently sitting in a replaced room. You're all caught up.
            </p>
          </div>
        }
        @for (group of resolvedGroups(); track group.addendumId) {
          @if (group.addendum) {
            <div class="border border-slate-200 rounded-md overflow-hidden p-4 shrink-0">
              <dsm-aps-addendum-preview
                class="flex-1 min-w-0 flex items-start"
                [dealAddendum]="group.addendum"
                [isExpanded]="isGroupExpanded(group.addendumId)"
                (toggleExpand)="toggleGroupExpanded(group.addendumId)"
                (onChanges)="onOptionChanges()"
              />

              @if (isGroupExpanded(group.addendumId)) {
                <div class="border-t border-outline-brown mt-3 pt-3">
                  <dsm-aps-addendum-options
                    [selectedProductId]="selectedAddendumId() === group.addendumId ? selectedProductId() : null"
                    [dealAddendum]="group.addendum"
                    [projectName]="
                      dealResource.value()?.project?.projectName ?? dealResource.value()?.project?.projectCode ?? null
                    "
                    [lotNo]="dealResource.value()?.lot?.lotNo ?? null"
                    [modelId]="modelId()"
                    [elevationId]="elevationId()"
                    [modelName]="dealResource.value()?.model?.modelName ?? null"
                    [elevationLabel]="dealResource.value()?.elevation?.elevationCode ?? null"
                    [buyerName]="dealResource.value()?.prospect?.name ?? null"
                    [mismatchedProductIds]="group.mismatchedProductIds"
                    [executedTriggerProductIds]="executedTriggerProductIds()"
                    [showAddOption]="false"
                    (onChanges)="onOptionChanges()"
                    (onProductSelected)="onAddendumProductSelect(group.addendumId, $event)"
                    (onKitProductSelected)="onAddendumProductSelect(group.addendumId, $event, true)"
                  />
                </div>
              }
            </div>
          }
        }
      </div>
    }
  </div>

  @if (showOptionDetails()) {
    <div
      [class]="
        isMobile()
          ? 'fixed inset-x-3 bottom-3 top-14 z-50 overflow-hidden rounded-xl'
          : 'w-80 xl:w-96 shrink-0 overflow-y-auto overflow-x-hidden self-start h-full'
      "
    >
      <dsm-aps-addendum-option-details
        [dealAddendum]="selectedAddendum()!"
        [addendumProductId]="selectedProductId()!"
        [isKitLinkedProduct]="selectedKitProductId === selectedProductId()"
        (onChanges)="onOptionChanges()"
        (onClose)="closeDetails()"
      />
    </div>
    @if (isMobile()) {
      <div class="fixed inset-0 bg-black/40 z-40" (click)="closeDetails()"></div>
    }
  }
</div>

<p-dialog
  header="Floor plan rule executed"
  [visible]="currentDisclosure() !== null"
  [modal]="true"
  [closable]="false"
  [dismissableMask]="false"
  appendTo="body"
  [style]="{ width: 'min(92vw, 32rem)' }"
>
  @if (currentDisclosure(); as execution) {
    <div class="flex flex-col gap-3 py-2">
      <i class="pi pi-exclamation-triangle text-amber-500 self-center" style="font-size: 2.5rem"></i>
      <p class="m-0 text-body-sm text-surface-700 text-left">
        <span class="font-semibold">{{ currentDisclosureLabel() }}</span>
        room replacement rule was executed, replacing
        <span class="font-semibold">{{ roomNames(execution.replacedRooms) }}</span>
        with
        <span class="font-semibold">{{ roomNames(execution.newRooms) }}</span
        >.
      </p>
    </div>
  }
  <ng-template pTemplate="footer">
    <p-button label="Acknowledge" (onClick)="acknowledgeCurrentDisclosure()" />
  </ng-template>
</p-dialog>
